/** Sentinel value for the "无配套" (belongs to no set) filter option. */
export const NO_SET = '__none__';

/** The product fields filtering cares about. */
export interface ProductLike {
    name: string;
    seller: string;
    price: number;
}

/** A product set as returned by getProductSets. `items` may be a JSON string or an array. */
export interface ProductSetLike {
    id: number;
    name: string;
    items: unknown;
}

/**
 * One product inside a set, after normalisation.
 * The last three are optional because items created before the package-set
 * editor existed do not carry them, and their absence must round-trip unchanged.
 */
export interface ProductSetItem {
    name: string;
    price: number;
    premium?: boolean;
    /** Set when the item was picked from the product dropdown. Older items match by name only. */
    productId?: number;
    /** Applied to the wallet when the resulting order completes. Negative reduces the payout. */
    walletAdjustment?: number;
    /** Seconds to confirm the order. `null` means use the 20s default. */
    orderConfirmTimeoutSeconds?: number | null;
}

/**
 * Normalise a set's `items` into a well-formed list.
 * The API returns it as either a JSON string or an already-parsed array, and
 * individual elements are not guaranteed well-formed. Anything unparseable
 * yields an empty list rather than throwing.
 */
export function parseSetItems(items: unknown): ProductSetItem[] {
    let raw: unknown = items;
    if (typeof raw === 'string') {
        try {
            raw = JSON.parse(raw);
        } catch {
            return [];
        }
    }
    if (!Array.isArray(raw)) {
        return [];
    }
    return raw
        .filter((el: any) => el && typeof el.name === 'string')
        .map((el: any) => {
            const item: ProductSetItem = {
                name: el.name,
                price: Number.isFinite(Number(el.price)) ? Number(el.price) : 0,
                premium: Boolean(el.premium),
            };
            // Only attach the extended keys when the source actually had them, so an
            // old-shape item round-trips to exactly the old shape. Emitting `0`/`null`
            // unconditionally would break the existing toEqual assertions.
            if (el.productId !== undefined && Number.isFinite(Number(el.productId))) {
                item.productId = Number(el.productId);
            }
            if (el.walletAdjustment !== undefined && Number.isFinite(Number(el.walletAdjustment))) {
                item.walletAdjustment = Number(el.walletAdjustment);
            }
            if (el.orderConfirmTimeoutSeconds !== undefined) {
                const n = Number(el.orderConfirmTimeoutSeconds);
                item.orderConfirmTimeoutSeconds =
                    el.orderConfirmTimeoutSeconds === null || !Number.isFinite(n) ? null : n;
            }
            return item;
        });
}

/** Every filter the product page understands. Values are strings straight from the inputs. */
export interface ProductFilters {
    name?: string;
    seller?: string;
    minPrice?: string;
    maxPrice?: string;
    set?: string;
}

/**
 * The sets a product belongs to. Membership is by product *name* appearing in the
 * set's `items`, because that is how the backend stores it.
 * A set with unparseable items matches nothing rather than throwing.
 */
export function setsForProduct<S extends ProductSetLike>(product: ProductLike, productSets: S[]): S[] {
    return productSets.filter(s =>
        parseSetItems(s.items).some(item => item.name === product.name));
}

/** True when the product satisfies every active filter. Empty filters are skipped. */
export function matchesFilters(
    product: ProductLike,
    filters: ProductFilters,
    productSets: ProductSetLike[],
): boolean {
    const name = (filters.name ?? '').trim().toLowerCase();
    if (name && !product.name.toLowerCase().includes(name)) {
        return false;
    }

    if (filters.seller && product.seller !== filters.seller) {
        return false;
    }

    // A blank or non-numeric bound leaves that side unbounded.
    const min = Number(filters.minPrice);
    if ((filters.minPrice ?? '').trim() && !Number.isNaN(min) && product.price < min) {
        return false;
    }

    const max = Number(filters.maxPrice);
    if ((filters.maxPrice ?? '').trim() && !Number.isNaN(max) && product.price > max) {
        return false;
    }

    if (filters.set) {
        const matched = setsForProduct(product, productSets);
        if (filters.set === NO_SET) {
            if (matched.length > 0) return false;
        } else if (!matched.some(s => String(s.id) === filters.set)) {
            return false;
        }
    }

    return true;
}

/** Narrow a product list to those matching the filters. */
export function filterProducts<P extends ProductLike>(
    products: P[],
    filters: ProductFilters,
    productSets: ProductSetLike[],
): P[] {
    return products.filter(p => matchesFilters(p, filters, productSets));
}

/** Distinct sellers present in the loaded products, sorted, for the 卖家 dropdown. */
export function sellerOptions(products: ProductLike[]): string[] {
    return Array.from(new Set(products.map(p => p.seller).filter(Boolean))).sort();
}

/** True when at least one filter would narrow the list. Drives the subtitle count. */
export function hasActiveFilters(filters: ProductFilters): boolean {
    return Boolean(
        (filters.name ?? '').trim() ||
        filters.seller ||
        (filters.minPrice ?? '').trim() ||
        (filters.maxPrice ?? '').trim() ||
        filters.set
    );
}
